// src/components/SimpleComponents/SimpleAvatarGroup.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { Tooltip } from 'antd';
import SimpleAvatar from './SimpleAvatar';
import useZustand from 'hooks/useZustand';
import {
  DEFAULT_BACK_COLOR_ACCENT,
  DEFAULT_BACK_COLOR_ACCENT_DARK,
  DEFAULT_FORE_COLOR,
  DEFAULT_FORE_COLOR_DARK,
} from 'constants/Constants';

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
/**
 * SimpleAvatarGroup - Overlapping avatar group using SimpleAvatar
 * Correctly applies backColor/foreColor for each member
 *
 * @param {Object}  props
 * @param {Array}   props.items       - Array of { key?, src?, name, backColor?, foreColor? }
 * @param {number}  props.maxCount    - Max avatars to show before "+N" (default: 3)
 * @param {number}  props.size        - Avatar size in pixels (default: 24)
 * @param {number}  props.overlap     - Negative overlap in pixels (default: size * 0.3)
 * @param {boolean} props.showBorder  - Show white border around avatars (default: true)
 * @param {boolean} props.showShadow  - Show drop shadow around avatars (default: true)
 * @param {Object}  props.style       - Additional styles for the wrapper div
 */
const SimpleAvatarGroup = ({
  items = [],
  maxCount = 3,
  size = 24,
  overlap,
  showBorder = true,
  showShadow = true,
  style = {},
}) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const currentTheme = useZustand('theme', 'currentTheme');
  const isDark = currentTheme === 'dark';

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. LOCAL VARIABLES
  // ─────────────────────────────────────────────────────────────────────────────
  const defaultBackColor = isDark ? DEFAULT_BACK_COLOR_ACCENT_DARK : DEFAULT_BACK_COLOR_ACCENT;
  const defaultForeColor = isDark ? DEFAULT_FORE_COLOR_DARK : DEFAULT_FORE_COLOR;
  const negativeMargin = -(overlap ?? Math.round(size * 0.3));
  const visible = items.slice(0, maxCount);
  const overflow = items.length - maxCount;

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. UI
  // ─────────────────────────────────────────────────────────────────────────────
  if (items.length === 0) return null;

  return (
    <div style={{ display: 'flex', alignItems: 'center', ...style }}>
      {visible.map((item, i) => (
        <Tooltip key={item.key ?? i} title={item.name || ''}>
          <span
            style={{
              display: 'inline-block',
              marginLeft: i > 0 ? negativeMargin : 0,
              zIndex: maxCount - i,
            }}
          >
            <SimpleAvatar
              size={size}
              src={item.src}
              name={item.name}
              backColor={item.backColor ?? defaultBackColor}
              foreColor={item.foreColor ?? defaultForeColor}
              borderColor={showBorder ? '#ffffff' : undefined}
              showBorder={showBorder}
              showShadow={showShadow}
            />
          </span>
        </Tooltip>
      ))}

      {/* "+N" bubble when items exceed maxCount */}
      {overflow > 0 && (
        <Tooltip
          title={
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {items.slice(maxCount).map((m, idx) => (
                <span key={m.key ?? idx}>{m.name}</span>
              ))}
            </div>
          }
        >
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: size,
              height: size,
              borderRadius: '50%',
              backgroundColor: defaultBackColor,
              color: defaultForeColor,
              fontSize: Math.max(9, Math.round(size * 0.4)),
              fontWeight: 600,
              marginLeft: negativeMargin,
              flexShrink: 0,
              zIndex: 0,
              border: showBorder ? '1px solid #ffffff' : 'none',
              boxShadow: showShadow ? '0 2px 6px rgba(0, 0, 0, 0.4)' : 'none',
            }}
          >
            +{overflow}
          </span>
        </Tooltip>
      )}
    </div>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default SimpleAvatarGroup;
